import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useStepper } from './hooks/useStepper';
import { useCourseSync } from './hooks/useCourseSync';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../../redux/actions/index';

import StepSelectDirection from './steps/StepSelectDirection';
import StepSelectCourse from './steps/StepSelectCourse';
import StepSelectTopics from './steps/StepSelectTopics';
import StepSelectStudents from './steps/StepSelectStudents';
import StepSummary from './steps/StepSummary';
import { hasAppLinked } from './helper';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';

import './SyncCourses.scss';

function SyncCoursesModal({ user, actions, onClose }) {
  const [submitted, setSubmitted] = useState(false);

  const app = user.user.profile.lmsApp;

  const hasLinkedWithApp = hasAppLinked(app, user);

  const { fullCourses, loading, fetchCompareData } = useCourseSync(
    actions,
    app,
  );

  const {
    step,
    goNext,
    goBack,
    resetStepper,
    selectedCourse,
    selectedTopics,
    selectedStudents,
    setSelectedCourse,
    setSelectedTopics,
    setSelectedStudents,
    direction,
    setDirection,
  } = useStepper();

  useEffect(() => {
    if (hasLinkedWithApp) {
      fetchCompareData();
    }
    // eslint-disable-next-line
  }, [hasLinkedWithApp]);

  const renderStep = () => {
    if (!direction)
      return <StepSelectDirection onSelect={setDirection} app={app} />;

    if (submitted) {
      return (
        <div>
          We are starting to sync. We will send you an email when the sync
          completes.
        </div>
      );
    }

    switch (step) {
      case 0:
        return (
          <StepSelectCourse
            loading={loading}
            courses={fullCourses}
            selectedCourse={selectedCourse}
            onSelect={(course) => {
              setSelectedCourse(course);
              setSelectedTopics(course.topics); // already inside course
              setSelectedStudents(course.students); // already inside course
            }}
          />
        );
      case 1:
        return (
          <StepSelectTopics
            loading={loading}
            topics={selectedCourse ? selectedCourse.topics : []}
            selectedTopics={selectedTopics}
            onChange={setSelectedTopics}
          />
        );
      case 2:
        return (
          <StepSelectStudents
            loading={loading}
            students={selectedCourse ? selectedCourse.students : []}
            selectedStudents={selectedStudents}
            onChange={setSelectedStudents}
          />
        );
      case 3:
        return (
          <StepSummary
            direction={direction}
            selectedCourse={selectedCourse}
            selectedTopics={selectedTopics}
            selectedStudents={selectedStudents}
            onConfirm={() => {
              // Trigger sync
              actions.syncCoursesToAverPoint(
                selectedCourse,
                selectedTopics,
                selectedStudents,
                app,
              );

              setSubmitted(true);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal show centered className="sync-courses-modal">
      <Modal.Header
        closeButton
        onHide={() => {
          onClose();
        }}
      >
        <h3>Sync Courses</h3>
      </Modal.Header>
      <Modal.Body>{renderStep()}</Modal.Body>
      {direction && step >= 0 && step < 3 && (
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end">
            <RectangularButton onClick={goBack} noBorder>
              Back
            </RectangularButton>
            <RectangularButton
              disabled={
                (step === 0 && !selectedCourse) ||
                (step === 1 && selectedTopics.length === 0)
              }
              onClick={goNext}
              darkButton
            >
              Next
            </RectangularButton>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SyncCoursesModal);
